import React, { useState, useEffect, useCallback } from "react";
import { DollarSign, Plus, XCircle, Ban, AlertTriangle, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Loading, SectionHeader, Card } from "./shared";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const SOURCES = [{ v: "manual", l: "Manual" }, { v: "provider_doc", l: "Provider Documentation" }];
const nowIso = () => new Date().toISOString();

function statusOf(p) {
  if (!p.is_active) return { l: "Deactivated", c: "bg-red-100 text-red-700" };
  const now = nowIso();
  if (p.effective_from > now) return { l: "Future", c: "bg-blue-100 text-blue-700" };
  if (p.effective_to && p.effective_to <= now) return { l: "Historical", c: "bg-slate-100 text-slate-600" };
  return { l: "Current", c: "bg-emerald-100 text-emerald-700" };
}
const fmtDate = (s) => (s ? new Date(s).toISOString().slice(0, 10) : "—");

const Th = ({ children, r }) => <th className={`whitespace-nowrap px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 ${r ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, r, b }) => <td className={`whitespace-nowrap px-3 py-2 text-sm ${r ? "text-right" : "text-left"} ${b ? "font-semibold text-[#17201A]" : "text-slate-600"}`}>{children}</td>;
const Field = ({ label, children }) => (<label className="block"><span className="mb-1 block text-xs font-semibold text-slate-500">{label}</span>{children}</label>);
const inp = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#3E7256] focus:outline-none";

export default function AdminAiPricing() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [f, setF] = useState({ provider: "", model: "", input_price_per_million: "", output_price_per_million: "", currency: "USD", effective_from: "", effective_to: "", pricing_source: "manual", source_reference: "", verified_at: "" });

  const load = useCallback(() => {
    setLoading(true); setError(false);
    api.get("/admin/ai-pricing").then((r) => setItems(r.data.pricing || [])).catch(() => setError(true)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = () => {
    setFormErr("");
    const body = { ...f };
    ["effective_to", "source_reference", "verified_at"].forEach((k) => { if (!body[k]) body[k] = null; });
    body.effective_from = f.effective_from ? new Date(f.effective_from).toISOString() : "";
    if (f.effective_to) body.effective_to = new Date(f.effective_to).toISOString();
    if (f.verified_at) body.verified_at = new Date(f.verified_at).toISOString();
    api.post("/admin/ai-pricing", body)
      .then(() => { setShowForm(false); setF({ ...f, provider: "", model: "", input_price_per_million: "", output_price_per_million: "" }); load(); })
      .catch((e) => setFormErr(e?.response?.data?.detail || "Échec de création."));
  };

  const closePeriod = (p) => {
    const d = window.prompt("Effective To (YYYY-MM-DD) — clôture la période, ne supprime pas l'enregistrement :");
    if (!d) return;
    api.patch(`/admin/ai-pricing/${p.id}/close`, { effective_to: new Date(d).toISOString() })
      .then(load).catch((e) => alert(e?.response?.data?.detail || "Échec de clôture."));
  };
  const deactivate = (p) => {
    if (!window.confirm("Désactiver ce tarif ? Les runs IA historiques associés pourraient devenir « non tarifés » dans Cost Intelligence. L'enregistrement est conservé (aucune suppression).")) return;
    api.patch(`/admin/ai-pricing/${p.id}/deactivate`).then(load).catch((e) => alert(e?.response?.data?.detail || "Échec."));
  };

  return (
    <div data-testid="admin-ai-pricing">
      <div className="flex items-start justify-between">
        <SectionHeader title="AI Provider Pricing" subtitle="Tarifs de coût provider (internes). Distinct des plans d'abonnement Lodge et du prix client." />
        <button onClick={() => { setShowForm((s) => !s); setFormErr(""); }} data-testid="ai-pricing-add-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#3E7256] px-3 py-2 text-sm font-semibold text-white hover:bg-[#345f49]">
          <Plus size={15} /> Ajouter un tarif
        </button>
      </div>

      {showForm && (
        <Card className="mb-5 p-5" data-testid="ai-pricing-form">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="Provider"><input className={inp} data-testid="ai-pricing-provider" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} placeholder="openai" /></Field>
            <Field label="Model"><input className={inp} data-testid="ai-pricing-model" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} placeholder="gpt-5.4" /></Field>
            <Field label="Currency"><input className={inp} data-testid="ai-pricing-currency" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} placeholder="USD" /></Field>
            <Field label="Input price / 1M tokens"><input className={inp} data-testid="ai-pricing-input" value={f.input_price_per_million} onChange={(e) => setF({ ...f, input_price_per_million: e.target.value })} placeholder="2.50" /></Field>
            <Field label="Output price / 1M tokens"><input className={inp} data-testid="ai-pricing-output" value={f.output_price_per_million} onChange={(e) => setF({ ...f, output_price_per_million: e.target.value })} placeholder="10.00" /></Field>
            <Field label="Pricing Source"><select className={inp} data-testid="ai-pricing-source" value={f.pricing_source} onChange={(e) => setF({ ...f, pricing_source: e.target.value })}>{SOURCES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select></Field>
            <Field label="Effective From"><input type="date" className={inp} data-testid="ai-pricing-from" value={f.effective_from} onChange={(e) => setF({ ...f, effective_from: e.target.value })} /></Field>
            <Field label="Effective To (vide = ouvert)"><input type="date" className={inp} data-testid="ai-pricing-to" value={f.effective_to} onChange={(e) => setF({ ...f, effective_to: e.target.value })} /></Field>
            <Field label="Verified At"><input type="date" className={inp} value={f.verified_at} onChange={(e) => setF({ ...f, verified_at: e.target.value })} /></Field>
            <Field label="Source Reference (URL)"><input className={inp} value={f.source_reference} onChange={(e) => setF({ ...f, source_reference: e.target.value })} placeholder="https://…" /></Field>
          </div>
          {f.provider && f.model && f.input_price_per_million && (
            <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500" data-testid="ai-pricing-summary">
              Résumé : <b>{f.provider}/{f.model}</b> · in {f.input_price_per_million} / out {f.output_price_per_million} {f.currency} par 1M · du {f.effective_from || "?"} au {f.effective_to || "ouvert"}
            </p>
          )}
          {formErr && <p className="mt-3 flex items-center gap-1.5 text-sm text-red-600" data-testid="ai-pricing-form-error"><AlertTriangle size={14} /> {formErr}</p>}
          <div className="mt-4 flex gap-2">
            <button onClick={submit} data-testid="ai-pricing-submit" className="rounded-lg bg-[#3E7256] px-4 py-2 text-sm font-semibold text-white hover:bg-[#345f49]">Créer le tarif</button>
            <button onClick={() => setShowForm(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Annuler</button>
          </div>
        </Card>
      )}

      {loading && <Loading />}
      {!loading && error && <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-10 text-center text-sm text-red-600" data-testid="ai-pricing-error">Impossible de charger les tarifs.</div>}
      {!loading && !error && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center" data-testid="ai-pricing-empty">
          <DollarSign size={20} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm text-slate-400">Aucun tarif provider configuré.</p>
          <button onClick={() => setShowForm(true)} className="mt-3 text-sm font-semibold text-[#3E7256]">Ajouter un tarif provider</button>
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <Card data-testid="ai-pricing-table">
          <SelectableTable rows={items} rowId={(p) => p.id} filename="ai-pricing.csv"
            ariaLabel="Tarifs IA — sélection et défilement"
            csvColumns={[
              { label: "Provider", get: (p) => p.provider },
              { label: "Model", get: (p) => p.model },
              { label: "Input / 1M", get: (p) => p.input_price_per_million },
              { label: "Output / 1M", get: (p) => p.output_price_per_million },
              { label: "Currency", get: (p) => p.currency },
              { label: "From", get: (p) => fmtDate(p.effective_from) },
              { label: "To", get: (p) => (p.effective_to ? fmtDate(p.effective_to) : "ouvert") },
              { label: "Status", get: (p) => statusOf(p).l },
              { label: "Source", get: (p) => p.pricing_source },
            ]}>
            <table className="w-full">
              <thead className="bg-slate-50"><tr>
                <Th><SelectAllHeaderCheckbox /></Th><Th>Provider</Th><Th>Model</Th><Th r>Input / 1M</Th><Th r>Output / 1M</Th><Th>Currency</Th>
                <Th>From</Th><Th>To</Th><Th>Status</Th><Th>Source</Th><Th r>Actions</Th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((p) => { const st = statusOf(p); return (
                  <tr key={p.id} data-testid="ai-pricing-row">
                    <Td><RowCheckbox id={p.id} label={`Sélectionner ${p.provider}/${p.model}`} /></Td>
                    <Td b>{p.provider}</Td><Td>{p.model}</Td>
                    <Td r>{p.input_price_per_million} </Td><Td r>{p.output_price_per_million}</Td><Td>{p.currency}</Td>
                    <Td>{fmtDate(p.effective_from)}</Td><Td>{p.effective_to ? fmtDate(p.effective_to) : "ouvert"}</Td>
                    <Td><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${st.c}`} data-testid="ai-pricing-status">{st.l}</span></Td>
                    <Td>{p.source_reference ? <a href={p.source_reference} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#3E7256]">{p.pricing_source}<ExternalLink size={11} /></a> : p.pricing_source}</Td>
                    <Td r>
                      {p.is_active && (!p.effective_to || p.effective_to > nowIso()) && (
                        <button onClick={() => closePeriod(p)} data-testid="ai-pricing-close-btn" className="mr-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-600"><XCircle size={13} /> Clôturer</button>
                      )}
                      {p.is_active && (
                        <button onClick={() => deactivate(p)} data-testid="ai-pricing-deactivate-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-red-600"><Ban size={13} /> Désactiver</button>
                      )}
                    </Td>
                  </tr>
                ); })}
              </tbody>
            </table>
          </SelectableTable>
        </Card>
      )}
    </div>
  );
}
